import { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import ProcessingTimeline from "@/components/documents/ProcessingTimeline";
import DuplicateDialog from "@/components/documents/DuplicateDialog";

/**
 * CanonicalUploader — the single reusable upload surface for Tax Flow.
 * Drag-and-drop + browse + multi-file, per-file status/progress, client-side
 * validation, retry, cancel, remove, and full keyboard accessibility.
 *
 * The component owns NO business/context logic. Each surface supplies an
 * `uploadFn(file, { onProgress, signal, asVersion }) => Promise<data>` that
 * injects its own destination/metadata and posts to the canonical pipeline.
 * A duplicate (409) should be thrown as an Error with `.duplicate = {existing}`.
 */
const STATUS = {
  queued: { label: "Queued", tone: "bg-slate-100 text-slate-600", icon: "Clock" },
  uploading: { label: "Uploading", tone: "bg-blue-100 text-blue-700", icon: "Loader2" },
  success: { label: "Uploaded", tone: "bg-emerald-100 text-emerald-700", icon: "CheckCircle2" },
  error: { label: "Failed", tone: "bg-rose-100 text-rose-700", icon: "AlertCircle" },
  duplicate: { label: "Duplicate", tone: "bg-amber-100 text-amber-700", icon: "Copy" },
  canceled: { label: "Canceled", tone: "bg-slate-100 text-slate-500", icon: "Ban" },
  invalid: { label: "Rejected", tone: "bg-rose-100 text-rose-700", icon: "ShieldAlert" },
};

const prettySize = (b) => {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0, v = b;
  while (v >= 1024 && i < 3) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

let _seq = 0;
const nextId = () => `f${Date.now()}_${_seq++}`;

export function CanonicalUploader({
  uploadFn,
  disabled = false,
  disabledHint = "Choose an upload destination to enable uploads.",
  accept = "",
  maxSizeMB = 50,
  multiple = true,
  autoStart = true,
  onFileUploaded,
  onAllComplete,
  compact = false,
  pollStatus,
  onView,
  allowSeparate = true,
  testId = "canonical-uploader",
}) {
  const [items, setItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [dupFor, setDupFor] = useState(null);
  const inputRef = useRef(null);
  const itemsRef = useRef([]);
  const pollTimers = useRef({});
  itemsRef.current = items;
  useEffect(() => () => { Object.values(pollTimers.current).forEach((t) => clearTimeout(t)); }, []);

  const acceptExts = accept
    ? accept.split(",").map((s) => s.trim().replace(/^\./, "").toLowerCase()).filter(Boolean)
    : [];

  const patch = useCallback((id, fields) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...fields } : it)));
  }, []);

  const validate = useCallback((file) => {
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024)
      return `File exceeds the ${maxSizeMB} MB limit.`;
    if (acceptExts.length) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!acceptExts.includes(ext)) return `.${ext} files are not allowed here.`;
    }
    return null;
  }, [maxSizeMB, acceptExts]);

  const startPolling = useCallback((id, docId) => {
    if (!pollStatus || !docId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const lc = await pollStatus(docId);
        patch(id, { lifecycle: lc });
        if (lc && lc.terminal_state && lc.terminal_state !== "processing") return;
      } catch (_) { /* transient — keep polling */ }
      if (attempts < 25) pollTimers.current[id] = setTimeout(tick, 1500);
    };
    pollTimers.current[id] = setTimeout(tick, 800);
  }, [pollStatus, patch]);

  const runUpload = useCallback(async (id, opts = {}) => {
    const it = itemsRef.current.find((x) => x.id === id);
    if (!it) return;
    const controller = new AbortController();
    patch(id, { status: "uploading", progress: 0, error: null, controller });
    try {
      const data = await uploadFn(it.file, {
        signal: controller.signal, asVersion: opts.asVersion, separate: opts.separate, note: opts.note,
        onProgress: (pct) => patch(id, { progress: pct }),
      });
      patch(id, { status: "success", progress: 100, controller: null, result: data, lifecycle: null });
      onFileUploaded && onFileUploaded(data);
      if (data && data.id) startPolling(id, data.id);
      const pending = itemsRef.current.some((x) =>
        x.id !== id && (x.status === "queued" || x.status === "uploading"));
      if (!pending && onAllComplete) onAllComplete();
    } catch (e) {
      if (controller.signal.aborted) { patch(id, { status: "canceled", controller: null }); return; }
      if (e && e.duplicate) {
        patch(id, { status: "duplicate", controller: null, existing: e.duplicate });
        setDupFor(id);
      } else {
        patch(id, { status: "error", controller: null, error: (e && e.message) || "Upload failed." });
      }
    }
  }, [uploadFn, patch, onFileUploaded, onAllComplete, startPolling]);

  const resolveDup = useCallback((choice, { note } = {}) => {
    const id = dupFor; setDupFor(null);
    const it = itemsRef.current.find((x) => x.id === id);
    if (!it) return;
    if (choice === "view") { if (onView && it.existing) onView(it.existing.id); }
    else if (choice === "version") runUpload(id, { asVersion: true, note });
    else if (choice === "separate") runUpload(id, { separate: true });
    else patch(id, { status: "canceled" });
  }, [dupFor, onView, runUpload, patch]);

  const addFiles = useCallback((fileList) => {
    if (disabled) return;
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const chosen = multiple ? arr : arr.slice(0, 1);
    const created = chosen.map((file) => {
      const invalid = validate(file);
      return {
        id: nextId(), file, name: file.name, size: file.size,
        status: invalid ? "invalid" : "queued", progress: 0,
        error: invalid || null, controller: null, existing: null, result: null,
      };
    });
    const next = multiple ? [...itemsRef.current, ...created] : created;
    itemsRef.current = next;
    setItems(next);
    if (autoStart) {
      created.filter((c) => c.status === "queued").forEach((c) => runUpload(c.id));
    }
  }, [disabled, multiple, validate, autoStart, runUpload]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    addFiles(e.dataTransfer?.files);
  }, [disabled, addFiles]);

  const onDragOver = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    if (!disabled) setDragActive(true);
  }, [disabled]);

  const onDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
  }, []);

  const openBrowse = useCallback(() => { if (!disabled) inputRef.current?.click(); }, [disabled]);

  const onKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") { e.preventDefault(); openBrowse(); }
  }, [disabled, openBrowse]);

  const cancel = useCallback((id) => {
    const it = itemsRef.current.find((x) => x.id === id);
    it?.controller?.abort();
  }, []);

  const remove = useCallback((id) => {
    const it = itemsRef.current.find((x) => x.id === id);
    it?.controller?.abort();
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const summary = items.reduce((a, it) => { a[it.status] = (a[it.status] || 0) + 1; return a; }, {});

  return (
    <div data-testid={testId}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label="Upload files: drag and drop here, or press Enter to browse"
        data-testid={`${testId}-dropzone`}
        onClick={openBrowse}
        onKeyDown={onKeyDown}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        className={`rounded-xl border-2 border-dashed text-center transition-colors outline-none ${compact ? "px-3 py-3" : "px-4 py-8"}
          ${disabled ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-70"
            : dragActive ? "border-blue-500 bg-blue-50/70 cursor-pointer"
              : "border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-500"}`}
      >
        {compact ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
            <Icons.UploadCloud className={`h-4 w-4 ${dragActive ? "text-blue-600" : "text-slate-400"}`} />
            {dragActive ? "Drop to upload" : <span>Drop or <span className="text-blue-600 font-medium">browse</span></span>}
          </div>
        ) : (
          <>
            <Icons.UploadCloud className={`h-8 w-8 mx-auto mb-2 ${dragActive ? "text-blue-600" : "text-slate-400"}`} />
            <div className="text-sm font-medium text-slate-700">
              {dragActive ? "Drop files to upload" : "Drag & drop files here"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              or <span className="text-blue-600 font-medium">browse</span>
              {multiple ? " — multiple files supported" : ""}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              {acceptExts.length ? `Allowed: ${acceptExts.join(", ")} · ` : ""}Max {maxSizeMB} MB per file
            </div>
          </>
        )}
        {disabled && <div className="text-[11px] text-amber-600 mt-2" data-testid={`${testId}-disabled-hint`}>{disabledHint}</div>}
        <input
          ref={inputRef} type="file" multiple={multiple} accept={accept}
          className="hidden" data-testid={`${testId}-input`} disabled={disabled}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {/* File queue */}
      {items.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`${testId}-list`}>
          {items.map((it) => {
            const s = STATUS[it.status] || STATUS.queued;
            const Ic = Icons[s.icon] || Icons.File;
            const active = it.status === "uploading";
            return (
              <div key={it.id} data-testid={`${testId}-file`} data-status={it.status}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="flex items-center gap-3">
                <Icons.FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-700 truncate" title={it.name}>{it.name}</span>
                    <span className="text-[11px] text-slate-400 shrink-0">{prettySize(it.size)}</span>
                  </div>
                  {active ? (
                    <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden" data-testid={`${testId}-progress`}>
                      <div className="h-full bg-blue-500 transition-all" style={{ width: `${it.progress || 0}%` }} />
                    </div>
                  ) : it.error ? (
                    <div className="text-[11px] text-rose-600 mt-0.5 truncate" title={it.error}>{it.error}</div>
                  ) : null}
                </div>
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${s.tone}`}>
                  <Ic className={`h-3 w-3 ${active ? "animate-spin" : ""}`} />{s.label}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  {it.status === "uploading" && (
                    <button type="button" onClick={() => cancel(it.id)} title="Cancel"
                      data-testid={`${testId}-cancel`} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                      <Icons.X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {it.status === "duplicate" && (
                    <button type="button" onClick={() => setDupFor(it.id)} title="Resolve duplicate"
                      data-testid={`${testId}-resolve-dup`} className="p-1 rounded hover:bg-amber-100 text-amber-700">
                      <Icons.GitCompare className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {it.status === "success" && onView && it.result?.id && it.lifecycle && it.lifecycle.terminal_state !== "processing" && (
                    <button type="button" onClick={() => onView(it.result.id)} title="View document"
                      data-testid={`${testId}-view`} className="p-1 rounded hover:bg-blue-100 text-blue-600">
                      <Icons.Eye className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {(it.status === "error" || it.status === "canceled") && (
                    <button type="button" onClick={() => runUpload(it.id, {})} title="Retry"
                      data-testid={`${testId}-retry`} className="p-1 rounded hover:bg-slate-100 text-blue-600">
                      <Icons.RotateCw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {it.status !== "uploading" && (
                    <button type="button" onClick={() => remove(it.id)} title="Remove"
                      data-testid={`${testId}-remove`} className="p-1 rounded hover:bg-slate-100 text-slate-400">
                      <Icons.Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                </div>
                {it.status === "success" && it.lifecycle && (
                  <ProcessingTimeline lifecycle={it.lifecycle} compact testId={`${testId}-timeline-${it.id}`} />
                )}
              </div>
            );
          })}
          <div className="text-[11px] text-slate-500 pt-0.5" data-testid={`${testId}-summary`}>
            {items.length} file{items.length > 1 ? "s" : ""}
            {summary.success ? ` · ${summary.success} uploaded` : ""}
            {summary.uploading ? ` · ${summary.uploading} uploading` : ""}
            {summary.error ? ` · ${summary.error} failed` : ""}
            {summary.duplicate ? ` · ${summary.duplicate} duplicate` : ""}
            {summary.invalid ? ` · ${summary.invalid} rejected` : ""}
          </div>
        </div>
      )}
      {dupFor && (() => {
        const it = itemsRef.current.find((x) => x.id === dupFor);
        return (
          <DuplicateDialog open existing={it?.existing} incomingName={it?.name}
            allowSeparate={allowSeparate} onResolve={resolveDup} />
        );
      })()}
    </div>
  );
}

export default CanonicalUploader;
